import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getArtistsForClient, deleteArtist, isAuthenticated, logout, importDefaultArtists } from '../../services/artistService';
import { getReleasesForClient, deleteRelease, importDefaultReleases } from '../../services/releaseService';
import { Artist, Release } from '../../types';
import { SectionHeader } from '../../components/ui/SectionHeader';
import { Plus, Trash2, LogOut, Music, Disc, Edit, Database } from 'lucide-react';

export const Dashboard: React.FC = () => {
    const [artists, setArtists] = useState<Artist[]>([]);
    const [releases, setReleases] = useState<Release[]>([]);
    const [activeTab, setActiveTab] = useState<'artists' | 'releases'>('artists');
    const [isImporting, setIsImporting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/admin/login');
            return;
        }
        loadData();
    }, [navigate]);

    const loadData = async () => {
        const fetchedArtists = await getArtistsForClient();
        setArtists(fetchedArtists);
        const fetchedReleases = await getReleasesForClient();
        setReleases(fetchedReleases);
    };

    const handleDeleteArtist = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this artist?')) {
            await deleteArtist(id);
            loadData();
        }
    };

    const handleDeleteRelease = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this release?')) {
            await deleteRelease(id);
            loadData();
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/admin/login');
    };

    const handleImportArtists = async () => {
        if (window.confirm('This will import the default artists into the database. Proceed?')) {
            setIsImporting(true);
            try {
                await importDefaultArtists();
                alert('Artists imported successfully!');
                loadData();
            } catch (error: any) {
                alert(error.message || 'Failed to import artists.');
            } finally {
                setIsImporting(false);
            }
        }
    };

    const handleImportReleases = async () => {
        if (window.confirm('This will import the default releases into the database. Proceed?')) {
            setIsImporting(true);
            try {
                await importDefaultReleases();
                alert('Releases imported successfully!');
                loadData();
            } catch (error: any) {
                alert(error.message || 'Failed to import releases.');
            } finally {
                setIsImporting(false);
            }
        }
    };

    const handleSyncSpotify = async () => {
        setIsImporting(true);
        try {
            const response = await fetch('/api/sync-spotify', { method: 'POST' });
            if (response.ok) {
                alert('Spotify sync triggered successfully! New releases (if any) have been added.');
                loadData();
            } else {
                const errorData = await response.json();
                alert(`Sync failed: ${errorData.error}`);
            }
        } catch (error: any) {
            alert('Failed to trigger Spotify sync.');
            console.error(error);
        } finally {
            setIsImporting(false);
        }
    };

    return (
        <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                    <SectionHeader title="Admin Dashboard" subtitle="Content Management" />
                    <div className="flex gap-4">
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                        >
                            <LogOut size={16} /> Logout
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-4 mb-8 border-b border-white/10 pb-4">
                    <button
                        onClick={() => setActiveTab('artists')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${activeTab === 'artists' ? 'bg-astral-purple text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                        <Music size={16} /> Artists
                    </button>
                    <button
                        onClick={() => setActiveTab('releases')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${activeTab === 'releases' ? 'bg-astral-purple text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                        <Disc size={16} /> Releases
                    </button>
                </div>

                {/* Artists Tab */}
                {activeTab === 'artists' && (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-white">Artists</h2>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleImportArtists}
                                    disabled={isImporting}
                                    className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors disabled:opacity-50"
                                >
                                    <Database size={16} /> Import Defaults
                                </button>
                                <Link
                                    to="/admin/add-artist"
                                    className="flex items-center gap-2 px-4 py-2 bg-astral-purple text-white rounded-md hover:bg-astral-purple/80 transition-colors"
                                >
                                    <Plus size={16} /> Add Artist
                                </Link>
                            </div>
                        </div>
                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {artists.map((artist) => (
                                <div key={artist.id} className="bg-black/50 border border-white/10 rounded-lg overflow-hidden flex flex-col">
                                    <div className="h-48 overflow-hidden relative">
                                        <img src={artist.image} alt={artist.name} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="p-4 flex-grow">
                                        <h3 className="text-xl font-bold text-white mb-1">{artist.name}</h3>
                                        <p className="text-astral-cyan text-sm mb-2">{artist.genre}</p>
                                        <p className="text-gray-400 text-sm line-clamp-3 mb-4">{artist.bio}</p>
                                    </div>
                                    <div className="p-4 border-t border-white/10 bg-black/30 flex justify-end gap-2">
                                        <Link
                                            to={`/admin/edit-artist/${artist.id}`}
                                            className="flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm font-bold uppercase tracking-wider"
                                        >
                                            <Edit size={14} /> Edit
                                        </Link>
                                        <button
                                            onClick={() => handleDeleteArtist(artist.id)}
                                            className="flex items-center gap-1 text-red-500 hover:text-red-400 text-sm font-bold uppercase tracking-wider"
                                        >
                                            <Trash2 size={14} /> Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Releases Tab */}
                {activeTab === 'releases' && (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-white">Releases</h2>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleSyncSpotify}
                                    disabled={isImporting}
                                    className="flex items-center gap-2 px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-600 transition-colors disabled:opacity-50"
                                >
                                    <Database size={16} /> Sync from Spotify
                                </button>
                                <button
                                    onClick={handleImportReleases}
                                    disabled={isImporting}
                                    className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors disabled:opacity-50"
                                >
                                    <Database size={16} /> Import Defaults
                                </button>
                                <Link
                                    to="/admin/add-release"
                                    className="flex items-center gap-2 px-4 py-2 bg-astral-purple text-white rounded-md hover:bg-astral-purple/80 transition-colors"
                                >
                                    <Plus size={16} /> Add Release
                                </Link>
                            </div>
                        </div>
                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {releases.map((release) => (
                                <div key={release.id} className="bg-black/50 border border-white/10 rounded-lg overflow-hidden flex flex-col">
                                    <div className="h-48 overflow-hidden relative">
                                        <img src={release.cover} alt={release.title} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="p-4 flex-grow">
                                        <h3 className="text-xl font-bold text-white mb-1">{release.title}</h3>
                                        <p className="text-astral-cyan text-sm mb-2">{release.artist}</p>
                                        <div className="flex gap-2 text-xs text-gray-400 mb-2">
                                            <span>{release.type}</span>
                                            <span>•</span>
                                            <span>{release.catalogNumber}</span>
                                        </div>
                                    </div>
                                    <div className="p-4 border-t border-white/10 bg-black/30 flex justify-end gap-2">
                                        <Link
                                            to={`/admin/edit-release/${release.id}`}
                                            className="flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm font-bold uppercase tracking-wider"
                                        >
                                            <Edit size={14} /> Edit
                                        </Link>
                                        <button
                                            onClick={() => handleDeleteRelease(release.id)}
                                            className="flex items-center gap-1 text-red-500 hover:text-red-400 text-sm font-bold uppercase tracking-wider"
                                        >
                                            <Trash2 size={14} /> Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
